import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ContractApplicationEntry } from '@access-cms/api-client';

/**
 * App-wide "which application am I acting on" selection, shared by the eNOVA
 * application and application-status pages.
 *
 * The selection lives outside React (sessionStorage + a subscriber set) so it
 * survives page-to-page navigation and full reloads within the session, even
 * though the application section's providers unmount when the user leaves it.
 */
const SELECTED_CONTRACT_STORAGE_KEY = 'access-cms:selected-contract-id';

const listeners = new Set<() => void>();

function subscribeToStoredContractId(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function readStoredContractId(): string | null {
  try {
    return window.sessionStorage.getItem(SELECTED_CONTRACT_STORAGE_KEY);
  } catch {
    return null;
  }
}

function getServerSnapshot(): null {
  return null;
}

/** Persist the app-wide selection. Pass null to clear it. */
export function setStoredContractId(contractId: string | null): void {
  try {
    if (contractId === null) {
      window.sessionStorage.removeItem(SELECTED_CONTRACT_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(SELECTED_CONTRACT_STORAGE_KEY, contractId);
    }
  } catch {
    // Storage unavailable — the selection still applies via the URL param.
  }

  listeners.forEach((listener) => listener());
}

export interface ApplicationSelection {
  /** Contract ids of every entry offered for selection. */
  candidateIds: string[];
  /** Make an explicit selection: persists app-wide and syncs the URL param. */
  selectContract: (contractId: string) => void;
  /** The resolved selection, or null when the user still has to choose. */
  selectedContractId: string | null;
}

/**
 * Resolve the selected contract for a set of per-contract application entries.
 *
 * Precedence:
 * 1. `?contractId=` URL param (deep links, e.g. from return emails) — adopted
 *    as the app-wide default when it names a known entry;
 * 2. the stored app-wide selection, which is written only after an explicit
 *    picker choice (or a valid deep link);
 * 3. the sole entry, when exactly one exists (pre-selected, not changeable);
 * 4. no selection when there is more than one entry and neither explicit
 *    source resolves to an available contract.
 *
 * Do not add a first-entry fallback for multiple candidates. The contract is
 * meaningful context, so a user must read and choose it before acting. Keep
 * this policy and its tests in sync if future product requirements change the
 * default-selection behavior.
 *
 * Ids that don't appear in `applications` are ignored rather than selected.
 */
export function useApplicationSelection(
  applications: Record<string, ContractApplicationEntry> | undefined,
): ApplicationSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlContractId = searchParams.get('contractId');
  const storedContractId = useSyncExternalStore(subscribeToStoredContractId, readStoredContractId, getServerSnapshot);

  const candidateIds = applications ? Object.keys(applications) : [];

  const validUrlId = urlContractId && applications?.[urlContractId] ? urlContractId : null;
  const validStoredId = storedContractId && applications?.[storedContractId] ? storedContractId : null;
  const soleCandidateId = candidateIds.length === 1 ? candidateIds[0] : null;
  const selectedContractId = validUrlId ?? validStoredId ?? soleCandidateId;

  // A deep link is an explicit choice — adopt it app-wide so the other
  // application pages land on the same selection. This is intentionally the
  // only mount-time write: multiple candidates otherwise remain unselected
  // until the user chooses one. Auto-selected sole candidates are NOT adopted:
  // the status page offers a narrower set (entries with applications only) and
  // must not clobber a broader choice.
  useEffect(() => {
    if (validUrlId && validUrlId !== storedContractId) {
      setStoredContractId(validUrlId);
    }
  }, [validUrlId, storedContractId]);

  const selectContract = useCallback(
    (contractId: string) => {
      setStoredContractId(contractId);

      // Keep the URL param in sync so a stale deep-link param cannot override
      // the new choice, and refresh preserves it.
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set('contractId', contractId);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { candidateIds, selectContract, selectedContractId };
}
